import { CarouselCore } from './core/CarouselCore';

export type MobileCarouselControllerOptions = {
    breakpoint?: number;
    carouselRoleDescription?: string;
    containerRole?: string;
    containerLabel?: string;
    labelNavPanel?: string;
    labelStatusMsg?: string;
};

/**
 * Vanilla TypeScript carousel controller.
 *
 * Expects the following pre-rendered HTML structure:
 *
 * ```html
 * <section class="sgw-mobile-carousel" aria-label="...">
 *   <div class="sgw-mobile-carousel__track">
 *     <div class="sgw-mobile-carousel__panel" data-carousel-panel>...</div>
 *     <div class="sgw-mobile-carousel__panel" data-carousel-panel>...</div>
 *
 *     <!-- or a list where each li becomes a panel -->
 *     <ul>
 *       <li>...</li>
 *       <li>...</li>
 *     </ul>
 *   </div>
 *   <div class="sgw-mobile-carousel__dots">
 *     <button class="sgw-mobile-carousel__dot" aria-label="Vis kort 1"></button>
 *     <button class="sgw-mobile-carousel__dot" aria-label="Vis kort 2"></button>
 *   </div>
 *   <span class="screen-reader-text status"></span>
 * </section>
 * ```
 *
 * Usage:
 * ```ts
 * const controller = new MobileCarouselController(document.querySelector('.sgw-mobile-carousel')!);
 * // Later, to clean up:
 * controller.destroy();
 * ```
 */
export class MobileCarouselController {
    private readonly core: CarouselCore;

    constructor(element: HTMLElement, options: MobileCarouselControllerOptions = {}) {
        const track = element.querySelector<HTMLDivElement>('.sgw-mobile-carousel__track');
        if (!track) {
            throw new Error('[MobileCarouselController] .sgw-mobile-carousel__track not found inside element');
        }

        const dots = element.querySelector<HTMLDivElement>('.sgw-mobile-carousel__dots');
        const announcer = element.querySelector<HTMLSpanElement>('.screen-reader-text.status');
        const directChildren = Array.from(track.children).filter(
            (child): child is HTMLElement => child instanceof HTMLElement,
        );

        const explicitPanels = directChildren.filter((child) => child.hasAttribute('data-carousel-panel'));

        const list = track.querySelector<HTMLElement>(':scope > ul, :scope > ol');
        const listPanels = list
            ? Array.from(list.children).filter(
                  (child): child is HTMLElement => child instanceof HTMLElement && child.tagName === 'LI',
              )
            : [];

        const panels =
            [explicitPanels, listPanels, directChildren].find((candidatePanels) => candidatePanels.length > 0) ??
            directChildren;

        panels.forEach((panel, index) => {
            panel.setAttribute('data-carousel-panel', '');
            panel.setAttribute('data-index', String(index));
        });

        const count = panels.length;

        this.core = new CarouselCore({
            container: element,
            track,
            dots,
            announcer,
            count,
            carouselRoleDescription: options.carouselRoleDescription,
            breakpoint: options.breakpoint,
            containerRole: options.containerRole,
            containerLabel: options.containerLabel,
            labelNavPanel: options.labelNavPanel,
            labelStatusMsg: options.labelStatusMsg,
        });
    }

    destroy(): void {
        this.core.destroy();
    }
}
